import {Reservation} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {Resource} from "crqlar-react-common/dist/models/resource/resource.model";
import {isSameDay} from "crqlar-react-common/dist/utils/date.util";
import {getLocalizedValue} from "crqlar-react-common/dist/utils/translations.util";
import {isStaffResource} from "../../../common/uiElements/utils";

/*
 * F13 Agenda helpers.
 *
 * STAFF vs FACILITY classification (Task 2 spike decision):
 * v3 has NO staff/facility classifier on a resource. A `Resource` (see
 * crqlar-react-common/src/models/resource/resource.model.ts) only carries
 * `name`, `nameLocalized`, `categoryIds`, `sectorIds`, `capacity`, `parentId`,
 * `meta`, etc. — there is no `resourceType` / `isStaff` field and no
 * STAFF/FACILITY enum. A `ResourceCategory`
 * (crqlar-react-common/src/models/resourceCategory/resourceCategory.model.ts)
 * only has `name` / `sectorIds` / `nameLocalized` — again no hard type enum.
 * This differs from v2, where Booking.resources[].category drove
 * getStaffForBooking / getFacilitiesForBooking.
 *
 * REVISED DECISION: there is still no hard classifier, but the admin spa-setup
 * wizard names a category "Staff", so we split staff vs facility heuristically
 * by category NAME (see collectStaffCategoryIds / getFilteredResourceNames
 * below). The ProfileAgenda toolbar exposes an "All resources / Staff / Facility"
 * selector instead of two toggles that showed the same combined list.
 */

export interface AgendaItem {
    /** start instant used for sort + grouping (expectedStartDate, with actualStartDate fallback) */
    date: Date;
    reservation: Reservation;
}

export interface AgendaDay {
    day: Date;
    items: AgendaItem[];
}

/** Print scope: the current guest, everyone in their room, or everyone in their group. */
export type AgendaScope = 'guest' | 'room' | 'group';

/** Multi-guest print layout: one merged timeline, or one section per guest. */
export type AgendaLayout = 'merged' | 'split';

export interface GuestAgenda {
    profileId: string;
    items: AgendaItem[];
}

const getStart = (r: Reservation): Date | undefined => r.expectedStartDate || r.actualStartDate;
const getEnd = (r: Reservation): Date | undefined => r.expectedEndDate || r.actualEndDate;

/** Drop reservations without a usable start+end, wrap as AgendaItem, sort ascending by start. */
export const buildAgendaItems = (reservations: Reservation[]): AgendaItem[] => {
    return reservations
        .filter(r => !!getStart(r) && !!getEnd(r))
        // expectedStartDate/actualStartDate may arrive as ISO strings over the wire — coerce to Date.
        .map(r => ({date: new Date(getStart(r) as Date), reservation: r}))
        .sort((a, b) => a.date.getTime() - b.date.getTime());
};

/** Group an already-sorted AgendaItem[] into day segments (mirrors v2 GuestAgenda.renderAgenda). */
export const groupAgendaItemsByDay = (items: AgendaItem[]): AgendaDay[] => {
    const days: AgendaDay[] = [];
    let current: AgendaDay | undefined;
    items.forEach(item => {
        if (!current || !isSameDay(current.day, item.date)) {
            current = {day: item.date, items: []};
            days.push(current);
        }
        current.items.push(item);
    });
    return days;
};

/**
 * Resolve a reservation's resource names from a lookup map (all resources,
 * ignoring category).
 */
export const getResourceNames = (
    reservation: Reservation,
    resourcesById: Map<string, Resource>
): string[] => {
    return (reservation.resourceIds || [])
        .map(id => resourcesById.get(id)?.name)
        .filter((n): n is string => !!n);
};

/*
 * STAFF vs FACILITY split (revised).
 *
 * v3 has no staff/facility type on Resource or ResourceCategory — only a
 * free-text category `name`. The admin spa-setup wizard, however, creates a
 * category literally named "Staff" (and "Facilities"). We therefore derive
 * "staff" heuristically: a resource is staff when one of its categories' names
 * contains "staff" (or the German "mitarbeiter"), case-insensitive. Everything
 * else is treated as a facility. The agenda also offers an "all" view that
 * ignores category. This is best-effort and depends on category naming.
 */

export type ResourceFilter = 'none' | 'all' | 'staff' | 'facility';

/**
 * Resource names for a reservation, filtered by the selected view and
 * localized to `language` (falls back to the raw name).
 * - 'none'     → no resources (hide the row)
 * - 'all'      → every resource, ignoring category
 * - 'staff'    → resources in a staff-named category
 * - 'facility' → everything that is not staff
 */
export const getFilteredResourceNames = (
    reservation: Reservation,
    resourcesById: Map<string, Resource>,
    staffCategoryIds: Set<string>,
    filter: ResourceFilter,
    language: string
): string[] => {
    if (filter === 'none') return [];
    return (reservation.resourceIds || [])
        .map(id => resourcesById.get(id))
        .filter((r): r is Resource => !!r)
        .filter(r => {
            if (filter === 'all') return true;
            const staff = isStaffResource(r, staffCategoryIds);
            return filter === 'staff' ? staff : !staff;
        })
        .map(r => getLocalizedValue(language, r.nameLocalized) || r.name)
        .filter((n): n is string => !!n);
};

/** Start/end instants for an agenda item, for the "HH:mm – HH:mm" time column. */
export const getItemTimeRange = (item: AgendaItem): {start?: Date; end?: Date} => ({
    start: getStart(item.reservation),
    end: getEnd(item.reservation),
});

/** First guest-viewable, non-hidden note text, or undefined. */
export const getGuestNote = (reservation: Reservation): string | undefined => {
    const note = reservation.notes?.find(n => n.guestViewable && !n.hidden && !!n.note);
    return note?.note || undefined;
};

/**
 * When the profile (main booker / app user) booked a reservation FOR another
 * person, that person's name is stored as free text on `reservationRequestData`
 * (the reservation stays owned by the booker). Returns the joined name, or
 * undefined when the reservation was booked for the profile itself.
 */
export const getBookedForName = (reservation: Reservation): string | undefined => {
    const req = reservation.reservationRequestData;
    const name = [req?.firstName, req?.lastName].filter(Boolean).join(' ').trim();
    return name || undefined;
};

/*
 * Room / Group membership helpers.
 *
 * IMPORTANT: room-mates and group members are derived ONLY from accommodation-
 * sector reservations (sectorType === ACCOMMODATION). A shared spa / restaurant /
 * activity booking in another sector must NOT be used to decide who shares a
 * room or group. The printed *content* per member is still their full agenda
 * across all sectors — that filtering happens elsewhere; these helpers only
 * establish membership.
 */

/** Keep only reservations whose sector is one of the accommodation sectors. */
export const filterAccommodationReservations = (
    reservations: Reservation[],
    accommodationSectorIds: Set<string>
): Reservation[] =>
    reservations.filter(r => !!r.sectorId && accommodationSectorIds.has(r.sectorId));

/** Union of profileIds across the given reservations, deduped, insertion-ordered. */
export const collectProfileIds = (reservations: Reservation[]): string[] => {
    const seen = new Set<string>();
    const out: string[] = [];
    reservations.forEach(r => (r.profileIds || []).forEach(id => {
        if (id && !seen.has(id)) {
            seen.add(id);
            out.push(id);
        }
    }));
    return out;
};

/** Distinct reservationGroupIds across the given reservations, insertion-ordered. */
export const collectGroupIds = (reservations: Reservation[]): string[] => {
    const seen = new Set<string>();
    const out: string[] = [];
    reservations.forEach(r => {
        const g = r.reservationGroupId;
        if (g && !seen.has(g)) {
            seen.add(g);
            out.push(g);
        }
    });
    return out;
};

/**
 * In-scope guest display names for a single item, in reservation order.
 * Used to label a merged-timeline row with "who" the item belongs to.
 * A shared reservation returns every in-scope guest on it.
 */
export const getItemGuestNames = (
    reservation: Reservation,
    profileNamesById: Map<string, string>,
    scopeProfileIds: Set<string>
): string[] =>
    (reservation.profileIds || [])
        .filter(id => scopeProfileIds.has(id))
        .map(id => profileNamesById.get(id))
        .filter((n): n is string => !!n);

/**
 * Split an item list into one bucket per in-scope guest, preserving the given
 * guest order. An item shared by N in-scope guests appears under each of them.
 */
export const groupAgendaItemsByGuest = (
    items: AgendaItem[],
    orderedProfileIds: string[]
): GuestAgenda[] =>
    orderedProfileIds.map(profileId => ({
        profileId,
        items: items.filter(it => (it.reservation.profileIds || []).includes(profileId)),
    }));
